import React, { type ReactElement, type ReactNode } from 'react';
import { FontAwesomeIcon, type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Link as ReactRouterLink } from 'react-router-dom';
import TooltipItem from 'atom-components/tooltip-item';
import styles from './index.module.scss';

type SharedTableActionProps = {
  tooltip: string;
  disabled?: boolean;
  isDangerous?: boolean;
};

type IconProp = Pick<FontAwesomeIconProps, 'icon'>;

type LinkProps = {
  href: string;
  openInNewTab?: boolean;
} & SharedTableActionProps &
  IconProp;

function Link({
  href,
  openInNewTab = false,
  tooltip,
  disabled,
  isDangerous,
  icon
}: LinkProps): ReactElement {
  const iconElement = <FontAwesomeIcon icon={icon} size="lg" className={styles.linkIcon} />;
  const linkClass = { [`${styles.link}`]: true };
  const dangerousClass = { [`${styles.dangerous}`]: isDangerous ?? false };

  return (
    <TooltipItem tooltip={tooltip}>
      {disabled ? (
        // If disabled is true, we should remove the href attribute, regardless of whether it is
        // passed as a prop, to prevent the link from functioning. The href attribute may be omitted
        // (as of HTML5) to create a placeholder link that resembles a traditional hyperlink, but
        // does not lead anywhere.
        // https://html.spec.whatwg.org/multipage/links.html#attr-hyperlink-href
        // https://w3c.github.io/html-aria/#example-communicate-a-disabled-link-with-aria
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a className={clsx(linkClass, dangerousClass)} role="link" aria-disabled>
          {iconElement}
        </a>
      ) : (
        <ReactRouterLink
          to={href}
          {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          className={clsx(linkClass, dangerousClass)}
        >
          {iconElement}
        </ReactRouterLink>
      )}
    </TooltipItem>
  );
}

type ButtonProps = {
  onClick: () => void;
} & SharedTableActionProps &
  Pick<FontAwesomeIconProps, 'icon'>;

function Button({ onClick, disabled, tooltip, isDangerous, icon }: ButtonProps): ReactElement {
  return (
    <TooltipItem tooltip={tooltip}>
      <button
        type="button"
        onClick={onClick}
        disabled={!!disabled}
        className={clsx(
          { [`${styles.button}`]: true },
          { [`${styles.dangerous}`]: isDangerous ?? false }
        )}
      >
        <FontAwesomeIcon icon={icon} className={styles.buttonIcon} />
      </button>
    </TooltipItem>
  );
}

type TableActionsProps = {
  children: ReactNode;
};

function TableActions({ children }: TableActionsProps): ReactElement {
  return <div className={styles.tableActions}>{children}</div>;
}

export default Object.assign(TableActions, {
  Link,
  Button
});
